import { useInfiniteQuery } from '@tanstack/react-query';
import { BuyerApi } from 'api/BuyerApi';
import axios from 'axios';
import { BuyerProductList } from 'components/buyer/products/Product/BuyerProductList';
import { FullScreenLoader } from 'components/shared/Loaders/FullScreenLoader';
import { AppContext } from 'contexts/user/AppContext';
import { useMergeProductsAndPricesSearchMain } from 'hooks/useMergeProductsAndPrices';
import { usePrevious } from 'hooks/usePrevious';
import {
  BuyerGroupedProductListPagedResult,
  GroupedProductModel,
} from 'models/buyer/product/BuyerGroupedProductListPagedResult';
import { LoginResponseUserModel } from 'models/identity/LoginResponseUserModel';
import { ProductModel } from 'models/shared/product/ProductModel';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';
import { Button, Spinner } from 'react-bootstrap';
import { CaretDownFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { getUrlParams } from 'utils/getUrlParams';

export const Search = () => {
  const { t } = useTranslation('components');

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);

  const [moreDealsLoading, setMoreDealsLoading] = useState<boolean>(false);

  const [totalPages, setTotalPages] = useState<number>(0);

  const [pages, setPages] = useState<BuyerGroupedProductListPagedResult[]>([]);

  const [totalProducts, setTotalProducts] = useState<number>(0);

  const [mounted, setMounted] = useState<boolean>(false);

  const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>(
    []
  );

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<string>('');
  const pageSize = 20;

  const prevValue = usePrevious(orderBy);

  const authUser = useAuthUser();
  const currentUser = authUser() as LoginResponseUserModel;
  const { appState, appContextDispatch } = useContext(AppContext);

  const searchString = getUrlParams('search') || '';
  const byCode = getUrlParams('ByCode') ?? false;

  const userLocaleId = appState.userProfile?.localization?.id || 1;

  /* TODO: Dennis - display for all sellers and not just first */
  const findProductsInfo = useCallback(
    async ({ pageParam = 1 }) => {
      setPageNumber(pageParam);
      let sellerIds =
        appState.buyerCurrentSuppliers === null
          ? null
          : appState.buyerCurrentSuppliers
              .filter(
                (supplier) => supplier.isSelected && !supplier.isMarketplace
              )
              .map((filteredSupplier) => filteredSupplier.value);

      appState.buyerCurrentSuppliers
        ?.filter((supplier) => supplier.isSelected && supplier.isMarketplace)
        .forEach((filteredSubSeller) =>
          filteredSubSeller.subSellers?.forEach((subSeller) =>
            sellerIds?.push(subSeller.value)
          )
        );

      if (
        appState.buyerProductsMainSearchSource &&
        typeof appState.buyerProductsMainSearchSource.cancel === 'function'
      ) {
        appState.buyerProductsMainSearchSource.cancel(
          'Operation canceled due to new request.'
        );
      }

      const source = axios.CancelToken.source();

      appContextDispatch({
        type: 'BUYER_SET_NEW_SOURCE_SEARCH_PRODUCTS',
        buyerProductsMainSearchSource: source,
      });

      // Make pagination
      const productsResponse = await BuyerApi.findProductsSimple(
        currentUser.companyId,
        sellerIds,
        searchString,
        prevValue !== orderBy ? 1 : pageParam,
        orderBy,
        pageSize,
        userLocaleId,
        false,
        selectedManufacturers
      );
      setTotalPages(productsResponse.totalPages);
      setTotalProducts(productsResponse.totalItems);

      setMoreDealsLoading(false);
      setIsInitialLoading(false);

      return productsResponse;
    },
    [
      appContextDispatch,
      appState.buyerCurrentSuppliers,
      appState.buyerProductsMainSearchSource,
      currentUser.companyId,
      orderBy,
      prevValue,
      searchString,
      selectedManufacturers,
      userLocaleId,
    ]
  );

  const findProductsPrices = useCallback(
    async ({ pageParam = 1 }) => {
      setPageNumber(pageParam);
      let sellerIds =
        appState.buyerCurrentSuppliers === null
          ? null
          : appState.buyerCurrentSuppliers
              .filter(
                (supplier) => supplier.isSelected && !supplier.isMarketplace
              )
              .map((filteredSupplier) => filteredSupplier.value);

      appState.buyerCurrentSuppliers
        ?.filter((supplier) => supplier.isSelected && supplier.isMarketplace)
        .forEach((filteredSubSeller) =>
          filteredSubSeller.subSellers?.forEach((subSeller) =>
            sellerIds?.push(subSeller.value)
          )
        );

      if (
        appState.buyerProductsMainSearchSource &&
        typeof appState.buyerProductsMainSearchSource.cancel === 'function'
      ) {
        appState.buyerProductsMainSearchSource.cancel(
          'Operation canceled due to new request.'
        );
      }

      const source = axios.CancelToken.source();

      appContextDispatch({
        type: 'BUYER_SET_NEW_SOURCE_SEARCH_PRODUCTS',
        buyerProductsMainSearchSource: source,
      });

      // Make pagination
      const productsResponse = await BuyerApi.findProductsGrouped(
        currentUser.companyId,
        sellerIds,
        searchString,
        prevValue !== orderBy ? 1 : pageParam,
        orderBy,
        pageSize,
        userLocaleId,
        false,
        selectedManufacturers,
        source.token
      );
      setTotalPages(productsResponse.totalPages);
      setTotalProducts(productsResponse.totalItems);

      setMoreDealsLoading(false);
      setIsInitialLoading(false);

      return productsResponse;
    },
    [
      appContextDispatch,
      appState.buyerCurrentSuppliers,
      appState.buyerProductsMainSearchSource,
      currentUser.companyId,
      orderBy,
      prevValue,
      searchString,
      selectedManufacturers,
      userLocaleId,
    ]
  );

  const pricesQuery = useInfiniteQuery(
    [
      `search_prices_${searchString}`,
      currentUser.companyId,
      userLocaleId,
      orderBy,
      appState.buyerCurrentSuppliers,
      selectedManufacturers,
    ],
    findProductsPrices,
    {
      enabled: !!currentUser.companyId,
      staleTime: 1000 * 60 * 30,
      getNextPageParam: (lastPage, pages) => lastPage.currentPage + 1,
      onSuccess(data) {
        const clonedPages: BuyerGroupedProductListPagedResult[] = [...pages];

        data.pages.forEach((page) => {
          const pageData = page.data;

          pageData.forEach((fetchedProduct) => {
            const matchedProduct = clonedPages[0]?.data.find(
              (product) => product.productCode === fetchedProduct.productCode
            );

            if (matchedProduct) {
              matchedProduct.products = fetchedProduct.products;
              matchedProduct.quantity = fetchedProduct.quantity;
            }
          });
        });

        setPages(clonedPages);
      },
    }
  );

  const productsQuery = useInfiniteQuery(
    [
      `search_info_${searchString}`,
      currentUser.companyId,
      userLocaleId,
      orderBy,
      appState.buyerCurrentSuppliers,
      selectedManufacturers,
    ],
    findProductsInfo,
    {
      enabled: !!currentUser.companyId,
      staleTime: 1000 * 60 * 30,
      getNextPageParam: (lastPage, pages) => lastPage.currentPage + 1,
      onSuccess(data) {
        const clonedPages: BuyerGroupedProductListPagedResult[] = [];

        data.pages.forEach((page) => {
          const clonedPageData: GroupedProductModel[] = [];

          page.data.forEach((product) => {
            const newProducts: ProductModel[] = [];
            product.products.forEach(
              (item) =>
                (appState.buyerCurrentSuppliers?.findIndex(
                  (supplier) =>
                    supplier.value === item.sellerCompanyId &&
                    supplier.isSelected &&
                    !supplier.isMarketplace
                ) !== -1 ||
                  appState.buyerCurrentSuppliers?.findIndex((supplier) =>
                    supplier.subSellers?.some(
                      (subSeller) =>
                        subSeller.value === item.sellerCompanyId &&
                        subSeller.isSelected
                    )
                  ) !== -1) &&
                newProducts.push({
                  id: item.id,
                  code: item.code,
                  name: item.name,
                  unit: '',
                  orderStep: 0,
                  manufacturerName: item.manufacturerName ?? '',
                  image: item.image ?? '',
                  isFavorite: false,
                  productClasses: [],
                  moqPrices: item.moqPrices,
                  stockLevels: [],
                  packageSize: 0,
                  sellerCompanyName: item.sellerCompanyName,
                  sellerCompanyId: item.sellerCompanyId,
                  sellerCompanyShortName: item.sellerCompanyShortName,
                })
            );

            const productGeneralInfo: GroupedProductModel = {
              productCode: product.productCode,
              products: newProducts,
            };

            clonedPageData.push(productGeneralInfo);
          });

          clonedPages.push({
            currentPage: 1,
            totalItems: 20,
            totalPages: 1,
            data: clonedPageData,
            parents: [],
          });
        });

        setPages(clonedPages);
      },
    }
  );

  useMergeProductsAndPricesSearchMain(productsQuery, pricesQuery, setPages);

  const loadMoreProducts = () => {
    setMoreDealsLoading(true);
    if (pageNumber < totalPages) {
      setPageNumber((prevState: number) => prevState + 1);
    }
    productsQuery.fetchNextPage();
    pricesQuery.fetchNextPage();
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const searchSummary = `${t('common.lbl_YouSearchedFor')} ${searchString}: ${
    productsQuery.data?.pages[0].totalItems
  } ${t('common.lbl_Products')}`;

  const BuyerBreadcrumbNode = (
    <h5 className='mt-2 ms-2 p-2 mb-0 pb-0 product-search-sticky-header'>
      {searchSummary}
    </h5>
  );

  return isInitialLoading ||
    (productsQuery.data && productsQuery.data?.pages?.length < 1) ? (
    <FullScreenLoader />
  ) : (
    <>
      <>
        {/* {mounted &&
          ReactDOM.createPortal(
            BuyerBreadcrumbNode,
            document &&
              document.getElementsByClassName('fixed-portal-receiver')[0]
          )} */}

        <BuyerProductList
          isLoading={productsQuery.isLoading}
          // getProducts={findProducts}
          isPricesLoading={pricesQuery.isLoading}
          breadcrumbNode={BuyerBreadcrumbNode}
          setPageNumber={setPageNumber}
          setOrderBy={setOrderBy}
          items={pages}
          setSelectedManufacturers={setSelectedManufacturers}
          selectedManufacturers={selectedManufacturers}
          productDetailRoute={'/buyer/product-details'}
        />
      </>

      <div className='d-flex justify-content-center mb-7'>
        {productsQuery.data &&
        (pageNumber < productsQuery.data?.pages[0].totalPages ||
          moreDealsLoading) ? (
          <Button
            disabled={moreDealsLoading}
            onClick={loadMoreProducts}
            className='btn btn-eleczap'
          >
            {t('productSearch.btn_LoadMoreProducts')}{' '}
            {moreDealsLoading ? (
              <Spinner animation='border' size='sm' />
            ) : (
              <CaretDownFill />
            )}
          </Button>
        ) : (
          !isInitialLoading && (
            <span>{t('productSearch.inf_AllProductsLoaded')}</span>
          )
        )}
      </div>
    </>
  );
};
